import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API } from "../../config";
import axios from "axios";
import { useSelector } from "react-redux";

// Fetch list of medicines
const fetchMedicines = async (userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };

    const { data } = await axios.get(
        `${API}/medicine-list/${userInfo._id}`,
        config
    );
    return data;
};

export const useListMedicines = () => {
    const { userInfo } = useSelector((state) => state.userLogin);

    return useQuery({
        queryKey: ['medicines'],
        queryFn: () => fetchMedicines(userInfo),
        enabled: !!userInfo,
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    });
};

// Create medicine
const createMedicine = async (med, userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };

    const { data } = await axios.post(
        `${API}/medicine-create/${userInfo._id}`,
        med,
        config
    );
    return data;
};

export const useCreateMedicine = () => {
    const queryClient = useQueryClient();
    const { userInfo } = useSelector((state) => state.userLogin);

    return useMutation({
        mutationFn: (med) => createMedicine(med, userInfo),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['medicines']);
        },
        onError: (error) => {
            console.error("Error creating medicine:", error);
        },
    });
};

// Update medicine
const updateMedicine = async (med, userInfo) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userInfo.token}`,
        },
    };

    const { data } = await axios.put(
        `${API}/medicine-update/${med._id}/${userInfo._id}`,
        med,
        config
    );
    return data;
};

export const useUpdateMedicine = () => {
    const queryClient = useQueryClient();
    const { userInfo } = useSelector((state) => state.userLogin);

    return useMutation({
        mutationFn: (med) => updateMedicine(med, userInfo),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['medicines']);
            queryClient.invalidateQueries(['medicine', data._id]);
        },
        onError: (error) => {
            console.error("Error updating medicine:", error);
        },
    });
};

// Delete medicine
const deleteMedicine = async (id, userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };

    await axios.delete(
        `${API}/medicine-remove/${id}/${userInfo._id}`,
        config
    );
};

export const useDeleteMedicine = () => {
    const queryClient = useQueryClient();
    const { userInfo } = useSelector((state) => state.userLogin);

    return useMutation({
        mutationFn: (id) => deleteMedicine(id, userInfo),
        onSuccess: () => {
            queryClient.invalidateQueries(['medicines']);
        },
        onError: (error) => {
            console.error("Error deleting medicine:", error);
        },
    });
};

// Get medicine details
const fetchMedicineDetails = async (id, userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };

    const { data } = await axios.get(
        `${API}/medicine-detail/${id}/${userInfo._id}`,
        config
    );
    return data;
};

export const useMedicineDetails = (id) => {
    const { userInfo } = useSelector((state) => state.userLogin);

    return useQuery({
        queryKey: ['medicine', id],
        queryFn: () => fetchMedicineDetails(id, userInfo),
        enabled: !!userInfo && !!id,
    });
};

// Get medicine type enums
const fetchMedicineTypeEnums = async (userInfo) => {
    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };

    const { data } = await axios.get(
        `${API}/medicine/medicine-type-values/${userInfo._id}`,
        config
    );
    return data;
};

export const useMedicineTypeEnums = () => {
    const { userInfo } = useSelector((state) => state.userLogin);

    return useQuery({
        queryKey: ['medicineTypeEnums'],
        queryFn: () => fetchMedicineTypeEnums(userInfo),
        enabled: !!userInfo,
    });
};